<template>
    <article class="product-card prd-card wholesale-card">
        <div class="product-card-top">
            <a :href="productUrl" class="product-image">
                <img :src="baseImage" :class="{ 'image-placeholder': !hasBaseImage }"
                    :alt="product.name" @error="onImageError" loading="lazy" width="240" height="180" />
            </a>
            <div class="product-card-actions">
                <button type="button" class="btn btn-wishlist" :class="{ added: inWishlist }"
                    :aria-label="$trans('storefront::product_card.wishlist')" :aria-pressed="inWishlist"
                    :title="$trans('storefront::product_card.wishlist')" @click="syncWishlist">
                    <i class="la-heart" :class="inWishlist ? 'las' : 'lar'" aria-hidden="true"></i>
                </button>
                <button type="button" class="btn btn-compare" :class="{ added: inCompareList }"
                    :aria-label="$trans('storefront::product_card.compare')" :aria-pressed="inCompareList"
                    :title="$trans('storefront::product_card.compare')" @click="syncCompareList">
                    <i class="las la-random" aria-hidden="true"></i>
                </button>
            </div>
            <ul class="list-inline product-badge">
                <li class="badge badge-primary" v-if="product.is_new">{{ $trans('storefront::product_card.new') }}</li>
                <li class="badge badge-danger" v-if="item.has_special_price">
                    <bdi v-if="item.has_percentage_special_price">-{{ item.special_price_percent }}%</bdi>
                    <span v-else>{{ $trans('storefront::categories.promotion') }}</span>
                </li>
            </ul>
        </div>
        <div class="product-card-middle">
            <a :href="productUrl" class="product-name" :title="product.name"><h6>{{ product.name }}</h6></a>
            <span class="wholesale-reference"><bdi>#{{ product.id }}</bdi></span>
            <product-rating v-if="product.reviews && product.reviews.length" :ratingPercent="product.rating_percent"
                :reviewCount="product.reviews.length"></product-rating>
        </div>
        <div class="product-card-bottom">
            <div class="wholesale-metadata">
                <span v-if="item.unit_value || item.unit" :title="$trans('storefront::product_card.packing')">
                    <i class="las la-box" aria-hidden="true"></i><bdi>{{ item.unit_value }} {{ item.unit }}</bdi>
                </span>
                <span :class="{ 'text-danger': item.is_out_of_stock, 'wholesale-stock': !item.is_out_of_stock }">
                    <i :class="item.is_out_of_stock ? 'las la-times-circle' : 'las la-check-circle'" aria-hidden="true"></i>
                    {{ $trans(item.is_out_of_stock ? 'storefront::product_card.out_of_stock' : 'storefront::categories.in_stock') }}
                </span>
                <span v-if="item.expried_date" :title="$trans('storefront::product_card.expried')">
                    <i class="las la-calendar" aria-hidden="true"></i><bdi>{{ item.expried_date }}</bdi>
                </span>
            </div>
            <div class="wholesale-pricing" v-if="item.formatted_price || validUnitPrice">
                <div class="product-price" v-html="item.formatted_price"></div>
                <span class="wholesale-unit-price" v-if="validUnitPrice">
                    {{ $trans('storefront::product_card.price_per_unit') }}: <bdi>{{ pricePerUnit }}</bdi>
                </span>
            </div>
            <div class="wholesale-area" v-if="allowToReport">
                <label :for="'area-' + _uid">{{ $trans('storefront::categories.warehouse_location') }}</label>
                <div class="input-group">
                    <input :id="'area-' + _uid" type="text" v-model="item.area" v-mask="'A-###-#-A'" :masked="true"
                        class="form-control" dir="ltr" placeholder="A-123-4-B" @focus="selectAll" />
                    <button type="button" class="btn btn-outline-primary" @click="updateProductArea"
                        :aria-label="$trans('storefront::categories.save')"><i class="las la-save" aria-hidden="true"></i></button>
                </div>
            </div>
            <div class="wholesale-cart" :aria-busy="addingToCart ? 'true' : 'false'">
                <template v-if="cartItem && cartItem.id">
                    <div class="wholesale-stepper">
                        <button type="button" :disabled="addingToCart || cartItem.qty <= 1"
                            :aria-label="$trans('storefront::categories.decrease')" @click="updateQuantity(Number(cartItem.qty) - 1)">
                            <i class="las la-minus" aria-hidden="true"></i>
                        </button>
                        <input type="number" min="1" step="1" :value="cartItem.qty" :max="quantityLimit"
                            :disabled="addingToCart" :aria-label="$trans('storefront::categories.quantity')"
                            @change="setQuantity($event)" />
                        <button type="button" :disabled="addingToCart || quantityLimit !== null && cartItem.qty >= quantityLimit"
                            :aria-label="$trans('storefront::categories.increase')" @click="updateQuantity(Number(cartItem.qty) + 1)">
                            <i class="las la-plus" aria-hidden="true"></i>
                        </button>
                    </div>
                    <button type="button" class="wholesale-remove" :disabled="addingToCart"
                        :aria-label="$trans('storefront::categories.remove')" :title="$trans('storefront::categories.remove')"
                        @click="updateQuantity(0)"><i class="las la-trash" aria-hidden="true"></i></button>
                </template>
                <a v-else-if="!isAuth" :href="loginUrl" class="btn btn-primary wholesale-cta">
                    <i class="las la-sign-in-alt" aria-hidden="true"></i>{{ $trans('storefront::categories.login_to_order') }}
                </a>
                <button v-else-if="hasNoOption || item.is_out_of_stock" type="button" class="btn btn-primary wholesale-cta"
                    :disabled="item.is_out_of_stock || addingToCart" @click="add">
                    <i class="las la-cart-plus" aria-hidden="true"></i>
                    {{ $trans(item.is_out_of_stock ? 'storefront::product_card.out_of_stock' : 'storefront::product_card.add_to_cart') }}
                </button>
                <a v-else :href="productUrl" class="btn btn-primary wholesale-cta">
                    <i class="las la-eye" aria-hidden="true"></i>{{ $trans('storefront::product_card.view_options') }}
                </a>
            </div>
        </div>
    </article>
</template>

<script>
import store from "../store";
import ProductCardMixin from "../mixins/ProductCardMixin";

export default {
    mixins: [ProductCardMixin],
    props: ["product"],
    data() {
        return { allowToReport: window.AyanCart.allowToReport };
    },
    computed: {
        item() { return { ...(this.product.variant ? this.product.variant : this.product) }; },
        loginUrl() { return route("login"); },
        validUnitPrice() {
            return this.isAuth && Number(this.product.unit_value) > 0 && Number.isFinite(Number(this.pricePerUnit));
        },
        quantityLimit() {
            if (!this.cartItem) return null;
            const stock = this.maxQuantity(this.cartItem);
            const limit = Number(this.product.max_qty_per_order);
            return limit > 0 ? (stock === null ? limit : Math.min(stock, limit)) : stock;
        },
    },
    mounted() { this.cartItem = store.getProductFromCart(this.product.id); },
    methods: {
        selectAll(event) { event.target.select(); },
        setQuantity(event) {
            let qty = Math.max(1, Math.floor(Number(event.target.value) || 1));
            if (this.quantityLimit !== null) qty = Math.min(qty, this.quantityLimit);
            event.target.value = qty;
            this.updateQuantity(qty);
        },
        async add() {
            try {
                await this.addToCart();
                this.cartItem = store.getProductFromCart(this.product.id);
            } catch (_) { /* The existing cart handler displays the error. */ }
        },
        onImageError(event) {
            const fallback = `${window.AyanCart.baseUrl}/build/assets/image-placeholder.png`;
            if (event.target.src !== new URL(fallback, window.location.origin).href) event.target.src = fallback;
        },
    },
};
</script>
